/**
 * SCORM 2004 Player component — renders SCORM content in an iframe.
 * Independent — no content-core dependency. Supports i18n via `language` prop.
 */

import React, { useMemo } from 'react';
import { ScormPlayerFrame } from '../ScormPlayerFrame';
import type { ScormLaunchConfig, ScormCompletionEvent } from '../types';
import { useScormTranslations } from '../i18n';
import type { Scorm2004Data, Scorm2004Answer } from './scorm2004-schema';

export interface Scorm2004PlayerProps {
    /** Content data. */
    data: Scorm2004Data;
    /** Session token for SCORM runtime API calls. */
    sessionToken?: string;
    /** Session ID. */
    sessionId?: string;
    /** Learner name. */
    learnerName?: string;
    /** Learner ID. */
    learnerId?: string;
    /** Called when the learner answers / completes. */
    onAnswer?: (answer: Scorm2004Answer) => void;
    /** UI language code (e.g. 'en', 'vi'). */
    language?: string;
    /** Whether interaction is disabled. */
    disabled?: boolean;
}

export function Scorm2004Player({
    data: rawData,
    sessionToken,
    sessionId,
    learnerName,
    learnerId,
    onAnswer,
    language,
}: Scorm2004PlayerProps) {
    const data = rawData ?? ({} as Scorm2004Data);
    const t = useScormTranslations(language);

    const launchConfig: ScormLaunchConfig | null = useMemo(() => {
        if (!data.packageId || !data.packageUrl || !data.launchUrl || !sessionId) {
            return null;
        }
        return {
            session_id: sessionId,
            content_id: '',
            package_id: data.packageId,
            package_base_url: data.packageUrl,
            launch_url: data.launchUrl,
            scorm_version: 'scorm-2004',
            manifest: (data.manifest ?? {}) as any,
            default_organization_id: data.defaultOrganizationId,
            endpoints: {
                commit_url: `/api/content-sessions/${sessionId}/scorm/commit`,
                runtime_url: `/api/content-sessions/${sessionId}/scorm/runtime`,
                runtime_all_url: `/api/content-sessions/${sessionId}/scorm/runtime/all`,
                heartbeat_url: `/api/content-sessions/${sessionId}/heartbeat`,
                submit_url: `/api/content-sessions/${sessionId}/submit`,
                close_url: `/api/content-sessions/${sessionId}/close`,
            },
        };
    }, [data, sessionId]);

    const handleComplete = (event: ScormCompletionEvent) => {
        onAnswer?.({
            completed: true,
            scoreRaw: event.scoreRaw,
            scoreScaled: event.scoreScaled,
            completionStatus: event.completionStatus,
            successStatus: event.successStatus,
            totalTime: event.totalTime,
        });
    };

    if (!launchConfig || !sessionToken) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                backgroundColor: '#f9fafb',
                borderRadius: 8,
                color: '#6b7280',
                fontSize: 14,
            }}>
                {!data.packageId ? t.playerNoPackage : t.playerNoSession}
            </div>
        );
    }

    return (
        <div className="scorm-2004-player" style={{ width: '100%' }}>
            <ScormPlayerFrame
                launchConfig={launchConfig}
                sessionToken={sessionToken}
                learnerName={learnerName}
                learnerId={learnerId}
                onComplete={handleComplete}
                height="600px"
                language={language}
            />
        </div>
    );
}
